import * as React from 'react';

/** Material UI Packages. */
import { Box, Drawer, IconButton, Tooltip } from '@mui/material';

/** Material UI Icons. */
import CloseIcon from '@mui/icons-material/Close';

/** Internal Components. */
import UpdateStatus from './UpdateStatus';

/**
 * Drawer width: wider than the Todo side panel (TodoPanel.js, 350px) so more of each status message
 * fits on one line. On a small screen it never covers more than 95% of the
 * viewport, leaving the grid visible behind the backdrop.
 */
export const STATUS_DRAWER_WIDTH = 650;
export const STATUS_DRAWER_MAX_WIDTH = '95vw';

/**
 * Slide-in panel that shows the same Status editor as the detail panel, but with the full
 * viewport height so more messages can be compared at once.
 *
 * It mounts a second `UpdateStatus` on top of the inline one. Both read the status list
 * from Redux and write through the same socket handlers, so an add/edit/delete/hide done
 * here shows up in the inline panel immediately, and websocket broadcasts from other users
 * reach the drawer the same way they reach the panel.
 *
 * Closing: the close button, a click on the backdrop and the ESC key all call `onClose`.
 */
const StatusDrawer = ({ open, onClose, row, statusConfig, selectedHouseRow, hblTabValue }) => {
    return (
        <Drawer
            open={open}
            onClose={onClose}
            anchor="right"
            ModalProps={{ slotProps: { backdrop: { 'data-testid': 'status-drawer-backdrop' } } }}
            PaperProps={{
                sx: {
                    width: STATUS_DRAWER_WIDTH,
                    maxWidth: STATUS_DRAWER_MAX_WIDTH,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    bgcolor: '#f8f8f8',
                },
            }}
            data-testid="status-drawer"
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    flexShrink: 0,
                    px: 2,
                    py: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: 'white',
                }}
            >
                <Tooltip title="Close">
                    <IconButton size="small" aria-label="close status drawer" onClick={onClose}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                {open && (
                    <UpdateStatus
                        row={row}
                        statusConfig={statusConfig}
                        selectedHouseRow={selectedHouseRow}
                        hblTabValue={hblTabValue}
                        variant="drawer"
                    />
                )}
            </Box>
        </Drawer>
    );
};

export default React.memo(StatusDrawer);
